// client/src/components/stats/CbetTextureBreakdown.tsx
//
// Muestra el breakdown de CBet Flop por textura × posición (IP/OOP).
// Solo cuenta HU flops — multiway queda excluido porque tiene benchmarks
// distintos y dinámica diferente.
//
// Layout: tabla con una row por (texture, pos) — 10 rows total.
// Columnas: Textura, Posición, CBet%, Healthy range, Sample, Status dot.

import React from 'react';
import { CbetTextureDerived } from '../../utils/pokerStats/types';
import { MIN_TEXTURE_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { evalBenchmark, MIN_SAMPLE_FOR_BENCHMARK_EVAL } from './benchmarks';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface CbetTextureBreakdownProps {
  cbetTexture: CbetTextureDerived[];
  label?: string;
}

export function CbetTextureBreakdown({ cbetTexture, label }: CbetTextureBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = cbetTexture.reduce((s, c) => s + c.opp, 0);

  if (totalOpp === 0) {
    return (
      <div className="cbet-texture-breakdown cbet-texture-empty">
        <p className="cbet-texture-empty-text">{t('stats.cbetTexture.empty')}</p>
      </div>
    );
  }

  const allLowSample = cbetTexture.every(c => c.opp === 0 || c.lowSample);

  // Human-friendly texture label
  const textureLabel = (tex: string): string => {
    switch (tex) {
      case 'dry':
        return t('stats.cbetTexture.texDry');
      case 'semi-wet':
        return t('stats.cbetTexture.texSemiWet');
      case 'wet':
        return t('stats.cbetTexture.texWet');
      case 'paired':
        return t('stats.cbetTexture.texPaired');
      case 'monotone':
        return t('stats.cbetTexture.texMonotone');
      default:
        return tex;
    }
  };

  return (
    <div className="cbet-texture-breakdown">
      {label && <div className="cbet-texture-title">{label}</div>}

      {allLowSample && (
        <div className="cbet-texture-warning">
          {t('stats.cbetTexture.warning', { n: MIN_TEXTURE_SAMPLE })}
        </div>
      )}

      <table className="cbet-texture-table">
        <thead>
          <tr>
            <th>{t('stats.cbetTexture.thTexture')}</th>
            <th>{t('stats.cbetTexture.thPosition')}</th>
            <th className="cbet-texture-th-num">{t('stats.cbetTexture.thCbet')}</th>
            <th className="cbet-texture-th-num">{t('stats.cbetTexture.thBenchmark')}</th>
            <th className="cbet-texture-th-num">{t('stats.cbetTexture.thSample')}</th>
            <th className="cbet-texture-th-status" />
          </tr>
        </thead>
        <tbody>
          {cbetTexture.map((c, idx) => {
            const benchmark = bm.getCbetTextureBenchmark(c.key);
            const canEvaluate = benchmark && c.opp >= MIN_SAMPLE_FOR_BENCHMARK_EVAL;
            const status = canEvaluate ? evalBenchmark(c.cbetPct, benchmark) : 'no-benchmark';

            // First row of each texture gets a border-top to visually group
            const isGroupStart = idx % 2 === 0;

            const rowClass = [
              'cbet-texture-row',
              c.lowSample ? 'cbet-texture-row-low' : '',
              isGroupStart ? 'cbet-texture-row-group-start' : '',
              status === 'warn' ? 'cbet-texture-row-warn' : '',
              status === 'critical' ? 'cbet-texture-row-critical' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <tr key={c.key} className={rowClass}>
                <td className="cbet-texture-cell-texture">
                  {isGroupStart ? textureLabel(c.texture) : ''}
                </td>
                <td className="cbet-texture-cell-pos">{c.posType.toUpperCase()}</td>
                <td className="cbet-texture-cell-num cbet-texture-cell-value">
                  {c.opp > 0 ? `${c.cbetPct}%` : '—'}
                </td>
                <td className="cbet-texture-cell-num cbet-texture-cell-benchmark">
                  {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
                </td>
                <td className="cbet-texture-cell-num cbet-texture-cell-sample">
                  {c.opp > 0 ? `n=${c.opp}` : '—'}
                  {c.lowSample && c.opp > 0 && (
                    <span className="cbet-texture-low-tag"> {t('stats.cbetTexture.lowTag')}</span>
                  )}
                </td>
                <td className="cbet-texture-cell-status">
                  {canEvaluate && status !== 'no-benchmark' && (
                    <span className={`stat-dot dot-${status}`} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="cbet-texture-disclaimer">{t('stats.cbetTexture.disclaimer')}</p>
    </div>
  );
}
